import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { push } from 'redux-first-history';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import * as exitModalContent from 'assets/content/consultation/exitModal';
import navBarContent from 'assets/content/navbar';

import useEmailCaptureExitPopupAbTest from 'abTesting/hooks/emailCaptureExitPopup';

import BackIcon from 'Components/BackIcon';
import BasicModal from 'Components/BasicModal';
import Button from 'Components/LegacyButton';
import Typography from 'Components/Typography';

import { getHaveCompletedHairProfile } from 'dux/user/selectors';
import * as flagVariants from 'dux/featureFlags/constants/flagVariants';
import * as consultationSelectors from 'dux/consultation/selectors';
import * as consultationActions from 'dux/consultation/actions';
import * as authSelectors from 'dux/auth/selectors';
import { trackHeapEvent } from 'dux/tracking/actions';

import EmailCaptureExitModal from './EmailCaptureExitModal';

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.s36}px;
`;

const ExitButton = styled(Link)`
  margin-top: 24px;
  color: ${({ theme }) => theme.palette.common.grey.dark};
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.grey.dark};
`;

const Container = styled.div`
  background: ${({ theme }) => theme.palette.backgrounds.white};
  box-shadow: 0 2px 3px 0 rgba(180, 180, 180, 0.2);
  display: flex;
  height: ${({ theme }) => theme.props.navbarHeight}px;
  width: 100%;
  overflow-y: hidden;
  align-items: center;
  justify-content: space-between;
  padding: 0px 60px;

  ${({ theme }) => theme.breakpoints.match('md')} {
    padding: 0px 20px;
  }
`;

const StyledButton = styled.button`
  ${({ theme }) => ({ ...theme.utils.resetButton })}
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.common.noir.medium};
  }
`;

const BackButton = styled(StyledButton)`
  display: flex;
  flex-direction: row;
`;

const StyledBackIcon = styled(BackIcon)`
  filter: brightness(0);
  margin-right: 7px;
`;

const Logo = styled.img`
  height: 55px;
  padding: 0px ${({ theme }) => theme.spacing.s24}px;

  ${({ theme }) => theme.breakpoints.match('sm')} {
    padding: 0px;
  }
`;

const ExitModal = ({ isOpen, onClose, hasCompleteHairProfile }) => {
  return (
    <BasicModal dataFrom="consultation-exit-modal" isOpen={isOpen} onClose={onClose}>
      <Typography align="center" paragraph variant="h2">
        {exitModalContent.title}
      </Typography>
      <Typography align="center" variant="p2">
        {exitModalContent.body}
      </Typography>
      <ButtonsContainer>
        <Button
          data-click="continue"
          data-from="consultation-exit-modal"
          data-testid="consultation-modal-continue-cta"
          noMargin
          onClick={onClose}
          variant="vert"
        >
          {exitModalContent.continueCTA}
        </Button>
        <ExitButton
          data-click="exit"
          data-from="consultation-exit-modal"
          data-testid="consultation-modal-exit-cta"
          to={hasCompleteHairProfile ? '/account' : '/'}
        >
          <Typography variant="p2">{exitModalContent.exitCTA}</Typography>
        </ExitButton>
      </ButtonsContainer>
    </BasicModal>
  );
};

ExitModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hasCompleteHairProfile: PropTypes.bool.isRequired,
};

const ConsultationNavBar = ({
  isAuthenticated,
  emailCaptureExitConsultationModalOpened,
  setEmailCaptureExitConsultationModalOpened,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasCompleteHairProfile = useSelector(getHaveCompletedHairProfile);
  const [isExitModalOpened, setExitModalOpened] = useState(false);
  const { isInExperiment, onChoice, variantName } = useEmailCaptureExitPopupAbTest();

  const exitConsultation = forcedUrl => {
    if (isInExperiment) {
      onChoice();
    }
    if (!isAuthenticated && variantName === flagVariants.VARIANT1) {
      setEmailCaptureExitConsultationModalOpened(true);
    } else if (hasCompleteHairProfile) {
      if (forcedUrl) {
        dispatch(push(forcedUrl));
      } else {
        dispatch(push(hasCompleteHairProfile ? '/account' : '/'));
      }
    } else {
      dispatch(trackHeapEvent('Consultation Exit Modal Viewed'));
      setExitModalOpened(true);
    }
  };

  return (
    <Container data-testid="consultation-header-nav">
      <BackButton
        aria-label="Navigate to previous screen"
        data-click="back"
        data-from="header-nav"
        data-testid="header-back-button"
        onClick={() => navigate(-1)}
        type="button"
      >
        <StyledBackIcon />
        <Typography color="noir" variant="p2">
          Back
        </Typography>
      </BackButton>

      <StyledButton
        aria-label="Return Home"
        data-click="logo"
        data-from="header-nav"
        data-testid="consultation-header-logo"
        onClick={() => exitConsultation('/')}
        type="button"
      >
        <Logo alt={navBarContent.logo.alt} src={navBarContent.logo.image} />
      </StyledButton>

      <StyledButton
        aria-label="Exit Consultation"
        data-click="exit"
        data-from="header-nav"
        data-testid="consultation-header-exit"
        onClick={() => exitConsultation()}
        type="button"
      >
        <Typography color="noir" variant="p2">
          Exit
        </Typography>
      </StyledButton>
      <EmailCaptureExitModal
        isOpen={emailCaptureExitConsultationModalOpened}
        onClose={() => setEmailCaptureExitConsultationModalOpened(false)}
      />
      <ExitModal
        hasCompleteHairProfile={hasCompleteHairProfile}
        isOpen={isExitModalOpened}
        onClose={() => setExitModalOpened(false)}
      />
    </Container>
  );
};

ConsultationNavBar.propTypes = {
  emailCaptureExitConsultationModalOpened: PropTypes.bool,
  isAuthenticated: PropTypes.bool.isRequired,
  setEmailCaptureExitConsultationModalOpened: PropTypes.func.isRequired,
};

ConsultationNavBar.defaultProps = {
  emailCaptureExitConsultationModalOpened: false,
};

export default connect(
  state => ({
    isAuthenticated: authSelectors.getIsAuthenticated(state),
    emailCaptureExitConsultationModalOpened: consultationSelectors.getEmailCaptureExitConsultationModalOpened(
      state
    ),
  }),
  {
    setEmailCaptureExitConsultationModalOpened:
      consultationActions.setEmailCaptureExitConsultationModalOpened,
  }
)(ConsultationNavBar);
